import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RoleBadge, permLabel, useStaffMeta } from "@/components/app/PermissionChecklist";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, ArrowUpDown } from "lucide-react";

const COLS = [["first_name", "Nome"], ["last_name", "Cognome"], ["email", "E-mail"], ["staff_role", "Ruolo"], ["active", "Stato"], ["created_at", "Data creazione"], ["last_login_at", "Ultimo accesso"]];
const dt = (v) => (v ? `${dateIt(v)} ${new Date(v).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : "—");

function PermBadges({ meta, row }) {
  if (row.staff_role === "amministratore") return <span className="text-xs text-slate-500">Accesso completo</span>;
  const shown = row.permissions.slice(0, 3);
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((p) => <span key={p} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700">{permLabel(meta, p)}</span>)}
      {row.permissions.length > 3 && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-200 text-slate-700">+{row.permissions.length - 3}</span>}
      {row.permissions.length === 0 && <span className="text-xs text-red-600">Nessun permesso</span>}
    </div>
  );
}

export default function AdminEmployees() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const meta = useStaffMeta();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const load = useCallback(() => api.get("/staff").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    const out = rows.filter((r) => (status === "all" || (status === "active") === !!r.active)
      && (!s || [r.first_name, r.last_name, r.email].some((v) => (v || "").toLowerCase().includes(s))));
    return out.sort((a, b) => {
      const x = String(a[sort.key] ?? ""); const y = String(b[sort.key] ?? "");
      return (sort.dir === "asc" ? 1 : -1) * x.localeCompare(y, "it");
    });
  }, [rows, q, status, sort]);
  const sortBy = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }));

  const del = async (r) => {
    if (!window.confirm(`Eliminare definitivamente ${r.first_name} ${r.last_name} (${r.email})?`)) return;
    try { await api.delete(`/staff/${r.id}`); toast.success("Dipendente eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-employees">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Elenco Dipendenti</h1>
          <p className="text-slate-500 mt-1">Gestisci gli account dello staff e i loro permessi di accesso.</p>
        </div>
        <Button asChild className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="staff-new-button"><Link to="/admin/dipendenti/nuovo"><Plus className="w-4 h-4 mr-1" /> Nuovo Dipendente</Link></Button>
      </div>
      <Card className="border-slate-200"><CardContent className="p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, cognome o e-mail" className="pl-9" data-testid="staff-search" />
        </div>
        <div className="w-48">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="staff-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem><SelectItem value="active">Attivi</SelectItem><SelectItem value="inactive">Disattivati</SelectItem></SelectContent>
          </Select>
        </div>
        <span className="text-sm text-slate-500" data-testid="staff-count">{list.length} account</span>
      </CardContent></Card>
      <Card className="border-slate-200"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            {COLS.map(([k, l]) => (
              <TableHead key={k}><button onClick={() => sortBy(k)} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid={`staff-sort-${k}`}>{l}<ArrowUpDown className={`w-3 h-3 ${sort.key === k ? "text-amber-600" : "text-slate-300"}`} /></button></TableHead>
            ))}
            <TableHead>Permessi</TableHead><TableHead>Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.map((r) => (
              <TableRow key={r.id} data-testid={`staff-row-${r.email}`}>
                <TableCell className="font-semibold">{r.first_name}</TableCell>
                <TableCell className="font-semibold">{r.last_name}</TableCell>
                <TableCell className="text-sm">{r.email}{r.id === user?.id && <span className="ml-1 text-[10px] text-amber-600 font-bold">(tu)</span>}</TableCell>
                <TableCell><RoleBadge role={r.staff_role} /></TableCell>
                <TableCell>{r.active ? <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">Attivo</span> : <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-slate-200 text-slate-600">Disattivato</span>}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{dateIt(r.created_at)}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{dt(r.last_login_at)}</TableCell>
                <TableCell className="min-w-[180px]"><PermBadges meta={meta} row={r} /></TableCell>
                <TableCell className="whitespace-nowrap">
                  <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/dipendenti/${r.id}`)} data-testid={`staff-edit-${r.email}`}><Pencil className="w-4 h-4" /></Button>
                  {r.id !== user?.id && <Button variant="ghost" size="icon" onClick={() => del(r)} data-testid={`staff-delete-${r.email}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-10">Nessun dipendente trovato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
